import React, { useState, useEffect } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { GetAdminConfig, UpdateAdminConfig, UploadMedia, resolveMediaUrl } from './../../../../utilities/BL/QuizzB9Zone';

/**
 * Componente interactivo para carga de archivos con Drag & Drop y vista previa.
 */
const DragAndDropField = ({
    fieldLabel,
    targetFieldName,
    mediaCategory,
    currentValue,
    onUploadFile,
    resolveMediaUrl,
    isProcessing = false,
    helpText = '',
    dimensionsText = '',
    acceptTypes = 'image/*',
    onClear = null
}) => {
    const [isDragHoverActive, setIsDragHoverActive] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragHoverActive(true);
    };

    const handleDragLeave = () => {
        setIsDragHoverActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragHoverActive(false);
        const selectedFile = e.dataTransfer.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
    };

    const handleNativeFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            onUploadFile(selectedFile, mediaCategory, targetFieldName);
        }
        e.target.value = '';
    };

    return (
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label fw-bold text-dark mb-0 small">
                    {fieldLabel}
                </label>
                {currentValue && onClear && (
                    <button
                        type="button"
                        className="btn btn-link text-danger p-0 small text-decoration-none fw-semibold"
                        onClick={() => onClear(targetFieldName)}
                        title="Quitar archivo"
                    >
                        <i className="bi bi-trash me-1"></i>Quitar imagen
                    </button>
                )}
            </div>

            <div
                className={`border border-2 border-dashed rounded-3 p-3 text-center cursor-pointer transition ${
                    isDragHoverActive ? 'border-danger bg-danger-subtle' : 'border-secondary-subtle bg-light'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => document.getElementById(`file-input-${targetFieldName}`).click()}
                style={{ minHeight: '95px', cursor: 'pointer' }}
            >
                {isProcessing ? (
                    <div className="py-2">
                        <div className="spinner-border spinner-border-sm text-danger me-2" role="status"></div>
                        <span className="small text-danger fw-bold">Subiendo y optimizando cintillo...</span>
                    </div>
                ) : (
                    <>
                        <i className="bi bi-cloud-arrow-up text-danger fs-3 d-block mb-1"></i>
                        <span className="small text-dark fw-semibold d-block">
                            Arrastra aquí tu imagen o haz <strong className="text-danger">clic para buscar</strong>
                        </span>
                        {dimensionsText && (
                            <span className="badge bg-secondary-subtle text-secondary small mt-1">
                                {dimensionsText}
                            </span>
                        )}
                        {helpText && <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>{helpText}</small>}
                    </>
                )}
                <input
                    id={`file-input-${targetFieldName}`}
                    type="file"
                    className="d-none"
                    accept={acceptTypes}
                    onChange={handleNativeFileChange}
                />
            </div>

            {currentValue && (
                <div className="mt-2 text-center bg-white p-2 rounded-3 border shadow-sm position-relative">
                    <div className="d-flex align-items-center justify-content-between px-2 mb-1">
                        <span className="badge bg-success-subtle text-success small fw-bold">
                            <i className="bi bi-check-circle me-1"></i>Imagen activa
                        </span>
                        <small className="text-muted text-truncate ms-2" style={{ fontSize: '0.75rem', maxWidth: '70%' }}>
                            {currentValue}
                        </small>
                    </div>
                    <img
                        src={resolveMediaUrl(currentValue)}
                        alt={fieldLabel}
                        style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain', background: '#1a1a1a', borderRadius: '4px', padding: '4px' }}
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export function QuizzB9ZoneConfigModal({ isOpen, onClose, onConfigUpdated }) {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [activeUploadFieldKey, setActiveUploadFieldKey] = useState('');

    const [formData, setFormData] = useState({
        matchTitle: 'Dorados de Sinaloa vs Rival',
        matchCode: 'ARA',
        passCodePrefix: 'FIEX',
        matchInstructions: 'Preséntate en el túnel 2 de acceso a cancha al finalizar el partido con tu código QR o captura de pantalla.',
        bannerImageMdUrl: '',
        bannerImageLgUrl: '',
        maxWinners: 50,
        timeLimitSeconds: '',
        isTriviaActive: true,
        gameType: 'DEVIL_WHACK',
        winnerSelectionMode: 'SCORE_THRESHOLD',
        minScoreRequired: 25,
        gameDurationSeconds: 15,
        offsetY: 0,
        passViewMode: 'COMPLETE',
        showPassQr: true,
        showPassUserData: false,
        showPassLocationNote: false
    });

    useEffect(() => {
        if (isOpen) {
            loadConfig();
        }
    }, [isOpen]);

    const loadConfig = async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminConfig();
            if (res.success && res.data) {
                setFormData({
                    matchTitle: res.data.matchTitle || '',
                    matchCode: res.data.matchCode || 'ARA',
                    passCodePrefix: res.data.passCodePrefix || 'FIEX',
                    matchInstructions: res.data.matchInstructions || '',
                    bannerImageMdUrl: res.data.bannerImageMdUrl || '',
                    bannerImageLgUrl: res.data.bannerImageLgUrl || '',
                    maxWinners: res.data.maxWinners || 50,
                    timeLimitSeconds: res.data.timeLimitSeconds ?? '',
                    isTriviaActive: res.data.isTriviaActive ?? true,
                    gameType: res.data.gameType || 'DEVIL_WHACK',
                    winnerSelectionMode: res.data.winnerSelectionMode || 'SCORE_THRESHOLD',
                    minScoreRequired: res.data.minScoreRequired ?? 25,
                    gameDurationSeconds: res.data.gameDurationSeconds || 15,
                    offsetY: res.data.offsetY || 0,
                    passViewMode: res.data.passViewMode || 'COMPLETE',
                    showPassQr: res.data.showPassQr ?? true,
                    showPassUserData: res.data.showPassUserData ?? false,
                    showPassLocationNote: res.data.showPassLocationNote ?? false
                });
            }
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al cargar la configuración de la jornada.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleUploadBannerFile = async (fileObject, mediaCategory, targetFieldName) => {
        setActiveUploadFieldKey(targetFieldName);
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Subiendo y optimizando cintillo...' });
        try {
            const uploadResponse = await UploadMedia(fileObject, mediaCategory);
            if (uploadResponse.success && uploadResponse.url) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Cintillo cargado con éxito!' });
                setFormData(prev => ({ ...prev, [targetFieldName]: uploadResponse.url }));
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: uploadResponse.message || 'Error al cargar el cintillo.' });
            }
        } catch (e) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado durante la carga.' });
        } finally {
            setActiveUploadFieldKey('');
        }
    };

    const handleClearBanner = (targetFieldName) => {
        setFormData(prev => ({ ...prev, [targetFieldName]: '' }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!formData.matchTitle.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El título de la jornada es obligatorio.' });
            return;
        }

        if (!formData.matchCode.trim()) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'El código del rival es obligatorio (ej. ARA, SAN, TIG).' });
            return;
        }

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                matchCode: formData.matchCode.trim().toUpperCase(),
                passCodePrefix: formData.passCodePrefix.trim().toUpperCase(),
                maxWinners: parseInt(formData.maxWinners, 10) || 50,
                minScoreRequired: parseInt(formData.minScoreRequired, 10) >= 0 ? parseInt(formData.minScoreRequired, 10) : 25,
                gameDurationSeconds: parseInt(formData.gameDurationSeconds, 10) > 0 ? parseInt(formData.gameDurationSeconds, 10) : 15,
                timeLimitSeconds: formData.timeLimitSeconds ? parseInt(formData.timeLimitSeconds, 10) : null,
                offsetY: parseInt(formData.offsetY, 10) || 0
            };

            const res = await UpdateAdminConfig(payload);
            if (res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Configuración de la jornada guardada correctamente!'
                });
                if (onConfigUpdated) onConfigUpdated(res.data);
                onClose();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: res.message || 'Error al guardar la configuración.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al guardar.' });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)', backdropFilter: 'blur(4px)', zIndex: 1060 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4">
                    <div className="modal-header bg-dark text-white border-bottom border-secondary px-4 py-3">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                            <i className="bi bi-gear-fill text-warning"></i>
                            Configuración de la Jornada y Partido
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close btn-close-white" 
                            aria-label="Close"
                            onClick={onClose}
                        ></button>
                    </div>

                    <div className="modal-body p-4 bg-light">
                        {isLoading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-danger" role="status">
                                    <span className="visually-hidden">Cargando...</span>
                                </div>
                                <p className="text-muted small mt-2">Cargando datos de configuración...</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSave} id="quizzB9ConfigForm">
                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-white mb-3">
                                    <h6 className="fw-bold text-danger border-bottom pb-2 mb-3">
                                        <i className="bi bi-trophy me-2"></i>Información del Encuentro
                                    </h6>
                                    
                                    <div className="row g-3">
                                        <div className="col-12 col-md-8">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Título del Partido / Jornada *
                                            </label>
                                            <input 
                                                type="text" 
                                                name="matchTitle"
                                                className="form-control"
                                                placeholder="Ej. Dorados de Sinaloa vs Santos Laguna"
                                                value={formData.matchTitle}
                                                onChange={handleChange}
                                                required
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Se mostrará en la tarjeta dorada del aficionado ganador.
                                            </small>
                                        </div>

                                        <div className="col-6 col-md-4">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Código Rival (3 letras) *
                                            </label>
                                            <input 
                                                type="text" 
                                                name="matchCode"
                                                className="form-control text-uppercase fw-bold"
                                                placeholder="ARA, SAN, TIG..."
                                                maxLength="5"
                                                value={formData.matchCode}
                                                onChange={handleChange}
                                                required
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Folio: #FIEX<strong>{formData.matchCode || 'RIV'}</strong>01
                                            </small>
                                        </div>

                                        <div className="col-6 col-md-6">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Prefijo Institucional de Folio
                                            </label>
                                            <input 
                                                type="text" 
                                                name="passCodePrefix"
                                                className="form-control text-uppercase fw-bold"
                                                placeholder="FIEX"
                                                maxLength="10"
                                                value={formData.passCodePrefix}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="col-6 col-md-6">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Cupo Máximo de Ganadores
                                            </label>
                                            <input 
                                                type="number" 
                                                name="maxWinners"
                                                className="form-control fw-bold"
                                                min="1"
                                                max="500"
                                                value={formData.maxWinners}
                                                onChange={handleChange}
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Límite global inviolable (ej. 50 pases).
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-white mb-3">
                                    <h6 className="fw-bold text-danger border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
                                        <span><i className="bi bi-controller me-2"></i>Dinámica, Minijuego y Criterio de Ganador</span>
                                        <span className="badge bg-danger text-white fw-bold">Parametrizable en Vivo</span>
                                    </h6>

                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Tipo de Dinámica / Juego
                                            </label>
                                            <select
                                                name="gameType"
                                                className="form-select fw-bold"
                                                value={formData.gameType}
                                                onChange={handleChange}
                                            >
                                                <option value="DEVIL_WHACK">🔨 Vence al Diablo / Dale un Guerretazo (Minijuego)</option>
                                                <option value="TRIVIA">❓ Trivia de Preguntas y Respuestas</option>
                                            </select>
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Define qué juego verán los aficionados en su pantalla.
                                            </small>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <label className="form-label fw-semibold small text-secondary">
                                                Modo de Selección de Ganadores
                                            </label>
                                            <select
                                                name="winnerSelectionMode"
                                                className="form-select fw-bold text-danger"
                                                value={formData.winnerSelectionMode}
                                                onChange={handleChange}
                                            >
                                                <option value="SCORE_THRESHOLD">🎯 Meta de Puntuación Mínima (Recomendado)</option>
                                                <option value="PARTICIPATION">🎟️ Participación Directa (Cualquiera gana hasta agotar cupos)</option>
                                                <option value="TRIVIA_PERFECT">🧠 Trivia Perfecta (100% aciertos)</option>
                                            </select>
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                Puedes cambiarlo en vivo durante el partido sin afectar pases ya emitidos.
                                            </small>
                                        </div>

                                        {formData.winnerSelectionMode === 'SCORE_THRESHOLD' && (
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">
                                                    Puntuación Mínima para Ganar Pase *
                                                </label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light fw-bold text-danger">≥</span>
                                                    <input 
                                                        type="number" 
                                                        name="minScoreRequired"
                                                        className="form-control fw-bold fs-6 text-danger"
                                                        placeholder="25"
                                                        min="1"
                                                        max="500"
                                                        value={formData.minScoreRequired}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    <span className="input-group-text bg-light text-muted small">puntos</span>
                                                </div>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    Si subes o bajas este valor, aplica instantáneamente a los nuevos envíos.
                                                </small>
                                            </div>
                                        )}

                                        {formData.gameType !== 'TRIVIA' && (
                                            <div className="col-12 col-md-6">
                                                <label className="form-label fw-semibold small text-secondary">
                                                    Duración de la Partida (Segundos)
                                                </label>
                                                <div className="input-group">
                                                    <input 
                                                        type="number" 
                                                        name="gameDurationSeconds"
                                                        className="form-control fw-bold"
                                                        placeholder="15"
                                                        min="5"
                                                        max="120"
                                                        value={formData.gameDurationSeconds}
                                                        onChange={handleChange}
                                                    />
                                                    <span className="input-group-text bg-light text-muted small">seg</span>
                                                </div>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    Tiempo límite del minijuego de topos en el celular (ej. 15 seg).
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-white mb-3">
                                    <h6 className="fw-bold text-danger border-bottom pb-2 mb-3 d-flex justify-content-between align-items-center">
                                        <span><i className="bi bi-card-heading me-2"></i>Formato y Elementos del Pase</span>
                                        {formData.passViewMode === 'CUSTOM' && (
                                            <span className="badge bg-warning text-dark fw-bold">Personalizado</span>
                                        )}
                                    </h6>

                                    {/* Presets Rápidos */}
                                    <label className="form-label fw-semibold small text-secondary mb-2">
                                        Plantillas Rápidas:
                                    </label>
                                    <div className="row g-3 mb-3">
                                        <div className="col-12 col-md-6">
                                            <div 
                                                className={`p-3 rounded-3 border h-100 transition-all ${formData.passViewMode === 'COMPLETE' ? 'border-danger bg-danger-subtle shadow-sm' : 'bg-light'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    passViewMode: 'COMPLETE',
                                                    showPassQr: true,
                                                    showPassUserData: false,
                                                    showPassLocationNote: false
                                                }))}
                                            >
                                                <div className="form-check">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="passViewMode" 
                                                        id="passModeComplete" 
                                                        value="COMPLETE" 
                                                        checked={formData.passViewMode === 'COMPLETE'}
                                                        onChange={() => {}}
                                                    />
                                                    <label className="form-check-label fw-bold text-dark fs-6 cursor-pointer" htmlFor="passModeComplete">
                                                        Opción Completa (Predeterminada)
                                                    </label>
                                                </div>
                                                <p className="small text-secondary mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                                                    Muestra el <strong>código QR de verificación</strong> y folio. Mantiene ocultos los datos personales y nota de acceso por defecto.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6">
                                            <div 
                                                className={`p-3 rounded-3 border h-100 transition-all ${formData.passViewMode === 'STANDARD' ? 'border-danger bg-danger-subtle shadow-sm' : 'bg-light'}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setFormData(prev => ({
                                                    ...prev,
                                                    passViewMode: 'STANDARD',
                                                    showPassQr: false,
                                                    showPassUserData: false,
                                                    showPassLocationNote: false
                                                }))}
                                            >
                                                <div className="form-check">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="radio" 
                                                        name="passViewMode" 
                                                        id="passModeStandard" 
                                                        value="STANDARD" 
                                                        checked={formData.passViewMode === 'STANDARD'}
                                                        onChange={() => {}}
                                                    />
                                                    <label className="form-check-label fw-bold text-dark fs-6 cursor-pointer" htmlFor="passModeStandard">
                                                        Opción Estándar (Visual Simple)
                                                    </label>
                                                </div>
                                                <p className="small text-secondary mt-2 mb-0" style={{ fontSize: '0.8rem' }}>
                                                    Formato clásico. Muestra únicamente el <strong>folio asignado y partido</strong>. Oculta el QR, datos y nota de acceso.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Configuración Granular de Elementos */}
                                    <div className="p-3 bg-light rounded-3 border">
                                        <label className="form-label fw-bold small text-dark d-block mb-2">
                                            <i className="bi bi-sliders me-1 text-danger"></i> Configuración granular de elementos visibles:
                                        </label>
                                        
                                        <div className="form-check form-switch mb-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                role="switch"
                                                id="switchShowPassQr"
                                                name="showPassQr"
                                                checked={formData.showPassQr}
                                                onChange={(e) => {
                                                    const nextVal = e.target.checked;
                                                    setFormData(prev => {
                                                        const updated = { ...prev, showPassQr: nextVal };
                                                        if (!updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'STANDARD';
                                                        } else if (updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'COMPLETE';
                                                        } else {
                                                            updated.passViewMode = 'CUSTOM';
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                            />
                                            <label className="form-check-label small fw-semibold text-dark cursor-pointer" htmlFor="switchShowPassQr">
                                                📱 Mostrar Código QR de Verificación (HMAC-SHA256)
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                role="switch"
                                                id="switchShowPassUserData"
                                                name="showPassUserData"
                                                checked={formData.showPassUserData}
                                                onChange={(e) => {
                                                    const nextVal = e.target.checked;
                                                    setFormData(prev => {
                                                        const updated = { ...prev, showPassUserData: nextVal };
                                                        if (!updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'STANDARD';
                                                        } else if (updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'COMPLETE';
                                                        } else {
                                                            updated.passViewMode = 'CUSTOM';
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                            />
                                            <label className="form-check-label small fw-semibold text-dark cursor-pointer" htmlFor="switchShowPassUserData">
                                                👤 Mostrar Datos del Aficionado (Nombre, Correo y Teléfono)
                                            </label>
                                        </div>

                                        <div className="form-check form-switch mb-0">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                role="switch"
                                                id="switchShowPassLocationNote"
                                                name="showPassLocationNote"
                                                checked={formData.showPassLocationNote}
                                                onChange={(e) => {
                                                    const nextVal = e.target.checked;
                                                    setFormData(prev => {
                                                        const updated = { ...prev, showPassLocationNote: nextVal };
                                                        if (!updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'STANDARD';
                                                        } else if (updated.showPassQr && !updated.showPassUserData && !updated.showPassLocationNote) {
                                                            updated.passViewMode = 'COMPLETE';
                                                        } else {
                                                            updated.passViewMode = 'CUSTOM';
                                                        }
                                                        return updated;
                                                    });
                                                }}
                                            />
                                            <label className="form-check-label small fw-semibold text-dark cursor-pointer" htmlFor="switchShowPassLocationNote">
                                                📍 Mostrar Leyenda "Túnel 2 al finalizar el partido"
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-white mb-3">
                                    <h6 className="fw-bold text-danger border-bottom pb-2 mb-3">
                                        <i className="bi bi-info-circle me-2"></i>Instrucciones de Acceso a Cancha
                                    </h6>
                                    <div>
                                        <textarea 
                                            name="matchInstructions"
                                            className="form-control"
                                            rows="3"
                                            placeholder="Indica la puerta, túnel o instrucciones de cita para los aficionados ganadores..."
                                            value={formData.matchInstructions}
                                            onChange={handleChange}
                                        ></textarea>
                                        <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                            Estas instrucciones aparecen en el recuadro destacado de la tarjeta dorada.
                                        </small>
                                    </div>
                                </div>

                                <div className="card border-0 shadow-sm rounded-3 p-3 bg-white mb-3">
                                    <h6 className="fw-bold text-danger border-bottom pb-2 mb-3">
                                        <i className="bi bi-image me-2"></i>Imágenes para Overlay OBS / Pantallas de Estadio
                                    </h6>
                                    
                                    <div className="row g-3">
                                        <div className="col-12 col-md-6">
                                            <DragAndDropField
                                                fieldLabel="Cintillo Mediano (OBS / Pantallas 1536px)"
                                                targetFieldName="bannerImageMdUrl"
                                                mediaCategory="banner_md"
                                                currentValue={formData.bannerImageMdUrl}
                                                onUploadFile={handleUploadBannerFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'bannerImageMdUrl'}
                                                dimensionsText="1536 x 180 px"
                                                helpText="Formato recomendado: PNG transparente o WEBP"
                                                onClear={handleClearBanner}
                                            />
                                        </div>
                                        <div className="col-12 col-md-6">
                                            <DragAndDropField
                                                fieldLabel="Cintillo Grande (Full HD 1920px)"
                                                targetFieldName="bannerImageLgUrl"
                                                mediaCategory="banner_lg"
                                                currentValue={formData.bannerImageLgUrl}
                                                onUploadFile={handleUploadBannerFile}
                                                resolveMediaUrl={resolveMediaUrl}
                                                isProcessing={activeUploadFieldKey === 'bannerImageLgUrl'}
                                                dimensionsText="1920 x 180 px"
                                                helpText="Formato recomendado: PNG transparente o WEBP"
                                                onClear={handleClearBanner}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>

                    <div className="modal-footer bg-white border-top px-4 py-3 d-flex justify-content-between">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                            disabled={isSaving}
                        >
                            Cancelar
                        </button>
                        <button 
                            type="submit" 
                            form="quizzB9ConfigForm"
                            className="btn btn-danger px-4 fw-bold d-flex align-items-center gap-2"
                            disabled={isSaving || isLoading}
                        >
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                    Guardando...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-circle-fill"></i>
                                    Guardar Configuración
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuizzB9ZoneConfigModal;
